import { UserAccount } from '../types';

export const ALL_RESTRICTED_AREAS = [
  { id: 'publishing-suite', name: 'Royal Publishing Suite', minRole: 'writer', icon: '👑', description: 'Draft, format, and seal official royal documents and decrees' },
  { id: 'financial-manager', name: 'Financial Management Module', minRole: 'admin', icon: '💰', description: 'Ledgers, double-entry bookkeeping, and royal treasury audit' },
  { id: 'admin-console', name: 'Global Admin Console & RBAC', minRole: 'admin', icon: '🏛️', description: 'User account provisioning, password reset controls, and global overrides' },
  { id: 'communication-portal', name: 'Messaging & Contacts Portal', minRole: 'writer', icon: '💬', description: 'Internal secretariat chat, royal press announcements, and contacts' },
  { id: 'resources-investment', name: 'Resources & Mineral Admin', minRole: 'writer', icon: '🪨', description: 'Solid mineral deposits and agricultural investment administrative views' },
  { id: 'raffle-manager', name: 'Raffle Machine & Tier Manager', minRole: 'writer', icon: '🎟️', description: 'Configure 4-tier raffles, ticket prices, prize tables, live draws & settlements' },
];

// ─── Initial Platform Accounts ────────────────────────────────────────────────
// ONLY the Global Administrator account is seeded at startup.
// All other accounts must be created by the Global Administrator via:
//   Staff Portal → Platform Admin Console → User Management
//
// ⚠️  SECURITY REMINDER: Rotate the admin password before December 24, 2026 launch
//     via: Platform Admin Console → User Management → Reset Password
export const INITIAL_DEMO_USERS: UserAccount[] = [
  {
    id: 'user-admin-00',
    email: 'erueuzairue@gmail.com',
    name: 'Hyginus Oshioke',
    title: 'Global Administrator — ERUE Festival Suite',
    role: 'admin',
    avatar: '👑',
    department: 'Platform Administration',
    status: 'active',
    password: '123Special@@',
    createdAt: '2026-01-01T00:00:00Z',
    mustChangePassword: false,
    initialLoginCompleted: true,
    passwordLastChanged: '2026-01-01T00:00:00Z',
    allowedRestrictedAreas: [
      'publishing-suite', 'financial-manager', 'admin-console',
      'communication-portal', 'resources-investment', 'raffle-manager',
    ],
  },
];

export const DEMO_USERS = INITIAL_DEMO_USERS;

// ⚠️  SECURITY: Rotate this password via Platform Admin Console before launch
export const DEMO_PASSWORDS: Record<string, string> = {
  'erueuzairue@gmail.com': '123Special@@',
};

const ACCOUNTS_DB_KEY = 'uzairue_user_accounts_db_v1';
const AUTH_STORAGE_KEY = 'uzairue_active_authenticated_user';

export function getUserAccounts(): UserAccount[] {
  let accounts: UserAccount[] = [];
  try {
    const raw = localStorage.getItem(ACCOUNTS_DB_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        accounts = parsed;
      }
    }
  } catch (err) {
    console.error('Failed to load user accounts database:', err);
  }

  if (accounts.length === 0) {
    accounts = [...INITIAL_DEMO_USERS];
  }

  // Ensure every user has default restricted area permissions if none set
  accounts = accounts.map((acc) => {
    let areas = acc.allowedRestrictedAreas;
    if (!areas || !Array.isArray(areas)) {
      if (acc.role === 'admin') {
        areas = ['publishing-suite', 'financial-manager', 'admin-console', 'communication-portal', 'resources-investment', 'raffle-manager'];
      } else {
        areas = ['publishing-suite', 'communication-portal', 'resources-investment', 'raffle-manager'];
      }
    }
    return {
      ...acc,
      allowedRestrictedAreas: areas,
      mustChangePassword: acc.mustChangePassword ?? false,
      initialLoginCompleted: acc.initialLoginCompleted ?? true,
    };
  });

  // Guarantee that Ikpemhi Oshioke (erueuzairue@gmail.com) always exists as Super Admin
  const ikpemhiExists = accounts.some(
    (a) => a.email.toLowerCase() === 'erueuzairue@gmail.com'
  );

  if (!ikpemhiExists) {
    accounts.unshift({
      id: 'user-admin-00',
      email: 'erueuzairue@gmail.com',
      name: 'Hyginus Oshioke',
      title: 'Global Administrator — ERUE Festival Suite',
      role: 'admin',
      avatar: '👑',
      department: 'Platform Administration',
      status: 'active',
      password: '123Special@@',
      createdAt: new Date().toISOString(),
      mustChangePassword: false,
      initialLoginCompleted: true,
      allowedRestrictedAreas: ['publishing-suite', 'financial-manager', 'admin-console', 'communication-portal', 'resources-investment', 'raffle-manager'],
    });
  } else {
    // Ensure admin role and active status for Ikpemhi Oshioke
    accounts = accounts.map((a) => {
      if (a.email.toLowerCase() === 'erueuzairue@gmail.com') {
        return {
          ...a,
          name: 'Hyginus Oshioke',
          role: 'admin',
          status: 'active',
          title: 'Global Administrator — ERUE Festival Suite',
        };
      }
      return a;
    });
  }

  saveAllUserAccounts(accounts);
  return accounts;
}


export function saveAllUserAccounts(accounts: UserAccount[]): void {
  try {
    localStorage.setItem(ACCOUNTS_DB_KEY, JSON.stringify(accounts));
  } catch (err) {
    console.error('Failed to save user accounts database:', err);
  }
}

export function upsertUserAccount(user: Partial<UserAccount> & { email: string; name: string; role: any }): UserAccount {
  const accounts = getUserAccounts();
  const existingIdx = accounts.findIndex((a) => a.id === user.id || a.email.toLowerCase() === user.email.toLowerCase());

  let updatedUser: UserAccount;
  const defaultAllowedAreas = user.role === 'admin' 
    ? ['publishing-suite', 'financial-manager', 'admin-console', 'communication-portal', 'resources-investment', 'raffle-manager']
    : ['publishing-suite', 'communication-portal', 'resources-investment', 'raffle-manager'];

  if (existingIdx >= 0) {
    updatedUser = {
      ...accounts[existingIdx],
      ...user,
      id: accounts[existingIdx].id,
      allowedRestrictedAreas: user.allowedRestrictedAreas || accounts[existingIdx].allowedRestrictedAreas || defaultAllowedAreas,
      mustChangePassword: user.mustChangePassword !== undefined ? user.mustChangePassword : accounts[existingIdx].mustChangePassword ?? false,
    };
    accounts[existingIdx] = updatedUser;
  } else {
    updatedUser = {
      id: user.id || `usr_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      email: user.email.trim().toLowerCase(),
      name: user.name.trim(),
      title: user.title || 'Secretariat Staff',
      role: user.role,
      avatar: user.avatar || (user.role === 'admin' ? '🏛️' : user.role === 'authorizer' ? '👑' : '✍️'),
      department: user.department || 'Royal Secretariat',
      status: user.status || 'active',
      password: user.password || 'uzairue2026',
      createdAt: new Date().toISOString(),
      mustChangePassword: user.mustChangePassword ?? true, // New users default to requiring password change
      initialLoginCompleted: false,
      allowedRestrictedAreas: user.allowedRestrictedAreas || defaultAllowedAreas,
      forcePasswordResetReason: user.forcePasswordResetReason || 'Initial user account provisioning by global administrator.',
    };
    accounts.push(updatedUser);
  }

  saveAllUserAccounts(accounts);

  // If updating the currently logged-in user, update session as well
  const currentUser = getStoredUser();
  if (currentUser && currentUser.id === updatedUser.id) {
    setStoredUser(updatedUser);
  }

  return updatedUser;
}

export function deleteUserAccount(userId: string): void {
  const accounts = getUserAccounts().filter((a) => a.id !== userId);
  saveAllUserAccounts(accounts);
}

export function toggleUserStatus(userId: string): UserAccount | null {
  const accounts = getUserAccounts();
  const target = accounts.find((a) => a.id === userId);
  if (target) {
    target.status = target.status === 'suspended' ? 'active' : 'suspended';
    saveAllUserAccounts(accounts);

    const currentUser = getStoredUser();
    if (currentUser && currentUser.id === target.id) {
      setStoredUser(target);
    }
    return target;
  }
  return null;
}

export function resetUserPassword(userId: string, newPass: string, mustChangeOnNextLogin: boolean = true, forceReason?: string): boolean {
  const accounts = getUserAccounts();
  const target = accounts.find((a) => a.id === userId);
  if (target) {
    target.password = newPass;
    target.mustChangePassword = mustChangeOnNextLogin;
    target.passwordLastChanged = new Date().toISOString();
    if (forceReason) {
      target.forcePasswordResetReason = forceReason;
    }
    saveAllUserAccounts(accounts);

    const currentUser = getStoredUser();
    if (currentUser && currentUser.id === target.id) {
      setStoredUser(target);
    }
    return true;
  }
  return false;
}

export function changeUserPassword(userId: string, currentPass: string, newPass: string): { success: boolean; message: string; updatedUser?: UserAccount } {
  const accounts = getUserAccounts();
  const target = accounts.find((a) => a.id === userId);

  if (!target) {
    return { success: false, message: 'User account not found.' };
  }

  if (target.password && target.password !== currentPass) {
    return { success: false, message: 'Current password provided is incorrect.' };
  }

  if (newPass.length < 6) {
    return { success: false, message: 'New password must be at least 6 characters long.' };
  }

  if (newPass === target.password) {
    return { success: false, message: 'New password must be different from current password.' };
  }

  target.password = newPass;
  target.mustChangePassword = false;
  target.initialLoginCompleted = true;
  target.passwordLastChanged = new Date().toISOString();
  delete target.forcePasswordResetReason;

  saveAllUserAccounts(accounts);

  const currentUser = getStoredUser();
  if (!currentUser || currentUser.id === target.id) {
    setStoredUser(target);
  }

  return { success: true, message: 'Password updated successfully.', updatedUser: target };
}

export function adminSetUserPasswordAndFlags(
  userId: string,
  newPass: string,
  mustChangePassword: boolean,
  allowedRestrictedAreas?: string[],
  forceReason?: string
): boolean {
  const accounts = getUserAccounts();
  const target = accounts.find((a) => a.id === userId);
  if (target) {
    if (newPass) {
      target.password = newPass;
      target.passwordLastChanged = new Date().toISOString();
    }
    target.mustChangePassword = mustChangePassword;
    if (allowedRestrictedAreas) {
      target.allowedRestrictedAreas = allowedRestrictedAreas;
    }
    if (forceReason) {
      target.forcePasswordResetReason = forceReason;
    } else if (!mustChangePassword) {
      delete target.forcePasswordResetReason;
    }
    saveAllUserAccounts(accounts);

    const currentUser = getStoredUser();
    if (currentUser && currentUser.id === target.id) {
      setStoredUser(target);
    }
    return true;
  }
  return false;
}

export function checkRestrictedAreaAccess(
  user: UserAccount | null,
  areaId: string
): { allowed: boolean; reason?: string; requiresPasswordReset?: boolean } {
  if (!user) {
    return { allowed: false, reason: 'Please sign in with authorized secretariat credentials.' };
  }

  if (user.status === 'suspended') {
    return { allowed: false, reason: 'This user account is currently suspended by the global administrator.' };
  }

  if (user.mustChangePassword) {
    return {
      allowed: false,
      requiresPasswordReset: true,
      reason: user.forcePasswordResetReason || 'An initial password change is required upon first login before accessing restricted areas.',
    };
  }

  if (user.role === 'admin') {
    return { allowed: true };
  }

  const allowedAreas = user.allowedRestrictedAreas || [];
  if (!allowedAreas.includes(areaId)) {
    return { allowed: false, reason: 'Your account has not been granted specific permission for this restricted area by the administrator.' };
  }

  return { allowed: true };
}

export function getStoredUser(): UserAccount | null {
  try {
    const saved = localStorage.getItem(AUTH_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (err) {
    console.error('Error retrieving authenticated user:', err);
  }
  return null;
}

export function setStoredUser(user: UserAccount | null): void {
  try {
    if (user) {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  } catch (err) {
    console.error('Error setting authenticated user session:', err);
  }
}


